import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Button } from '@atoms/index';
import { cn } from '@/utils/cn';
import {
  adminSupportService,
  SupportTicket,
  SupportTicketStatus,
  SupportTicketPriority,
  AdminUpdateTicketDto,
} from '@services/supportService';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  OPEN: 'Açık',
  IN_PROGRESS: 'İşlemde',
  RESOLVED: 'Çözüldü',
  CLOSED: 'Kapatıldı',
};

const PRIORITY_LABELS: Record<SupportTicketPriority, string> = {
  LOW: 'Düşük',
  MEDIUM: 'Orta',
  HIGH: 'Yüksek',
  URGENT: 'Acil',
};

const STATUS_BADGE: Record<SupportTicketStatus, 'default' | 'info' | 'warning' | 'success'> = {
  OPEN: 'info',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  CLOSED: 'default',
};

const PRIORITY_COLORS: Record<SupportTicketPriority, string> = {
  LOW: 'text-gray-500',
  MEDIUM: 'text-orange-500',
  HIGH: 'text-amber-600',
  URGENT: 'text-red-600',
};

const VALID_TRANSITIONS: Record<SupportTicketStatus, SupportTicketStatus[]> = {
  OPEN: ['IN_PROGRESS', 'CLOSED'],
  IN_PROGRESS: ['RESOLVED', 'CLOSED'],
  RESOLVED: ['CLOSED', 'IN_PROGRESS'],
  CLOSED: [],
};

function TicketDetailModal({
  ticket,
  onClose,
  onUpdated,
}: {
  ticket: SupportTicket;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [dto, setDto] = useState<AdminUpdateTicketDto>({
    status: ticket.status,
    priority: ticket.priority,
    resolutionNotes: ticket.resolutionNotes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (update: AdminUpdateTicketDto) =>
      adminSupportService.updateTicket(ticket.id, update),
    onSuccess: () => {
      onUpdated();
      onClose();
    },
    onError: () => setError('Güncelleme başarısız. Lütfen tekrar deneyin.'),
  });

  const handleSave = () => {
    setError(null);
    if (dto.status === 'RESOLVED' && !dto.resolutionNotes?.trim()) {
      setError('RESOLVED durumu için çözüm notu zorunludur');
      return;
    }
    mutation.mutate(dto);
  };

  const allowedStatuses = VALID_TRANSITIONS[ticket.status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-base font-bold text-gray-900">Talep Detayı</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-gray-900">{ticket.subject}</h3>
              <Badge variant={STATUS_BADGE[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{ticket.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-0.5">Kullanıcı ID</p>
              <p className="font-mono text-gray-800 text-xs truncate">{ticket.userId}</p>
            </div>
            {ticket.orderId && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-0.5">Sipariş ID</p>
                <p className="font-mono text-gray-800 text-xs truncate">{ticket.orderId}</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-0.5">Oluşturulma</p>
              <p className="text-gray-800 text-xs">
                {new Date(ticket.createdAt).toLocaleString('tr-TR')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-0.5">Son Güncelleme</p>
              <p className="text-gray-800 text-xs">
                {new Date(ticket.updatedAt).toLocaleString('tr-TR')}
              </p>
            </div>
          </div>

          {ticket.status !== 'CLOSED' && (
            <div className="space-y-4 pt-2 border-t border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Güncelle</p>

              {allowedStatuses.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Durum</label>
                  <div className="flex gap-2 flex-wrap">
                    {([ticket.status, ...allowedStatuses] as SupportTicketStatus[]).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setDto((d) => ({ ...d, status: s }))}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                          dto.status === s
                            ? 'bg-[#E63946] text-white border-[#E63946]'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        )}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Öncelik</label>
                <div className="flex gap-2 flex-wrap">
                  {(['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as SupportTicketPriority[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setDto((d) => ({ ...d, priority: p }))}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                        dto.priority === p
                          ? 'bg-gray-800 text-white border-gray-800'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      )}
                    >
                      {PRIORITY_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  Çözüm Notu{dto.status === 'RESOLVED' && <span className="text-red-500 ml-1">*</span>}
                </label>
                <textarea
                  value={dto.resolutionNotes ?? ''}
                  onChange={(e) => setDto((d) => ({ ...d, resolutionNotes: e.target.value }))}
                  rows={3}
                  placeholder="Çözüm notunu buraya yazın..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent resize-none"
                />
              </div>

              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="flex gap-3">
                <Button variant="default" className="flex-1" onClick={onClose}>
                  İptal
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={handleSave}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SupportTab = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | 'ALL'>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<SupportTicketPriority | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  const statsQuery = useQuery({
    queryKey: ['admin-ticket-stats'],
    queryFn: () => adminSupportService.getStats(),
  });

  const ticketsQuery = useQuery({
    queryKey: ['admin-tickets', statusFilter, priorityFilter, search],
    queryFn: () =>
      adminSupportService.listTickets({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        priority: priorityFilter === 'ALL' ? undefined : priorityFilter,
        search: search.trim() || undefined,
        limit: 50,
      }),
  });

  const handleUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['admin-ticket-stats'] });
  };

  const stats = statsQuery.data;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
          {[
            { label: 'Toplam', value: stats.total, color: 'text-gray-900' },
            { label: 'Açık', value: stats.open, color: 'text-blue-600' },
            { label: 'İşlemde', value: stats.inProgress, color: 'text-amber-600' },
            { label: 'Çözüldü', value: stats.resolved, color: 'text-green-600' },
            { label: 'Kapatıldı', value: stats.closed, color: 'text-gray-500' },
            { label: 'Acil', value: stats.urgent, color: 'text-red-600' },
          ].map((s) => (
            <Card key={s.label}>
              <div className="text-center">
                <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Konu veya açıklama ara..."
            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SupportTicketStatus | 'ALL')}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent bg-white"
          >
            <option value="ALL">Tüm Durumlar</option>
            {(Object.keys(STATUS_LABELS) as SupportTicketStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as SupportTicketPriority | 'ALL')}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent bg-white"
          >
            <option value="ALL">Tüm Öncelikler</option>
            {(Object.keys(PRIORITY_LABELS) as SupportTicketPriority[]).map((p) => (
              <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
      </Card>

      {ticketsQuery.isLoading && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]" />
        </div>
      )}

      {ticketsQuery.isError && (
        <Card>
          <p className="text-center text-red-500 py-8">Talepler yüklenemedi.</p>
        </Card>
      )}

      {!ticketsQuery.isLoading && !ticketsQuery.isError && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl overflow-hidden shadow-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Konu</th>
                <th className="text-left font-semibold px-4 py-3">Durum</th>
                <th className="text-left font-semibold px-4 py-3">Öncelik</th>
                <th className="text-left font-semibold px-4 py-3">Tarih</th>
                <th className="text-right font-semibold px-4 py-3">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(ticketsQuery.data?.tickets.length ?? 0) === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-500" colSpan={5}>
                    Talep bulunamadı
                  </td>
                </tr>
              )}
              {ticketsQuery.data?.tickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 max-w-xs truncate">{ticket.subject}</p>
                    <p className="text-xs text-gray-400 truncate max-w-xs">{ticket.description}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_BADGE[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('font-medium', PRIORITY_COLORS[ticket.priority])}>
                      {PRIORITY_LABELS[ticket.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {new Date(ticket.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="default" onClick={() => setSelectedTicket(ticket)}>
                      İncele
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
};

export default SupportTab;
